import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TrainingContent.css';
import PresentationPlayer from './PresentationPlayer';
import HandsOnExercise from './components/HandsOnExercise';
import { presentations } from './presentations-v2';
import { quizQuestions } from './data/quizQuestions';

export default function TrainingContent({ userId, userEmail }) {
  const { t } = useTranslation();
  const [selectedModule, setSelectedModule] = useState(null);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  // Training modules - loaded from database
  const [modules, setModules] = useState([]);

  useEffect(() => {
    // Load training modules and user progress
    loadTrainingData();
  }, [userId]);

  const loadTrainingData = async () => {
    try {
      // Load training modules for compliance-program track
      const modulesResponse = await fetch('/api/training/modules?track=compliance-program');
      let modulesData = [];

      if (modulesResponse.ok) {
        modulesData = await modulesResponse.json();
      }

      // Load user progress if userId provided
      if (userId && modulesData.length > 0) {
        const progressResponse = await fetch(`/api/training/progress?userId=${userId}`);
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setProgress(progressData);

          // Mark completed content in modules
          if (progressData.contentProgress) {
            const enrichedModules = updateModulesWithProgress(modulesData, progressData.contentProgress);
            setModules(enrichedModules);
          } else {
            setModules(modulesData);
          }
        } else {
          setModules(modulesData);
        }
      } else {
        setModules(modulesData);
      }
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateModulesWithProgress = (modulesData, contentProgress) => {
    return modulesData.map(module => ({
      ...module,
      content: module.content.map(lesson => {
        const progress = contentProgress.find(cp => cp.id === lesson.id);
        return {
          ...lesson,
          completed: progress?.completed_at ? true : false,
          score: progress?.score
        };
      })
    }));
  };

  const handleModuleSelect = (module) => {
    setSelectedModule(module);
  };

  const handleContentComplete = async (moduleId, contentId) => {
    try {
      // Find the module to check if all lessons are now complete
      const module = modules.find(m => m.id === moduleId);
      const isLastLesson = module && module.content.every(c =>
        c.id === contentId || c.completed
      );

      const response = await fetch('/api/training/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, moduleId, contentId, isLastLesson })
      });

      if (response.ok) {
        // Update local state
        const updatedModules = modules.map(m => {
          if (m.id === moduleId) {
            return {
              ...m,
              content: m.content.map(c =>
                c.id === contentId ? { ...c, completed: true } : c
              )
            };
          }
          return m;
        });
        setModules(updatedModules);

        // If all lessons are complete, auto-mark training as complete
        if (isLastLesson) {
          console.log('[Training] All lessons completed for module:', moduleId);
        }
      }
    } catch (error) {
      console.error('Error marking content complete:', error);
    }
  };

  const calculateProgress = (module) => {
    const completed = module.content.filter(c => c.completed).length;
    return Math.round((completed / module.content.length) * 100);
  };

  if (loading) {
    return <div className="loading">Loading training modules...</div>;
  }

  return (
    <div className="training-content">
      <div className="training-container">
        {/* Sidebar: Module List */}
        <aside className="modules-sidebar">
          <h2>{t('training.modules')}</h2>
          <div className="modules-list">
            {modules.map(module => (
              <div
                key={module.id}
                className={`module-card ${selectedModule?.id === module.id ? 'active' : ''}`}
                onClick={() => handleModuleSelect(module)}
              >
                <div className="module-header">
                  <span className="module-icon">{module.icon}</span>
                  <div className="module-info">
                    <h3>{module.title}</h3>
                    <p className="module-category">{module.category}</p>
                  </div>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${calculateProgress(module)}%` }}
                  ></div>
                </div>
                <span className="progress-text">{calculateProgress(module)}%</span>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="training-main-content">
          {selectedModule ? (
            <div className="module-viewer">
              <div className="module-header-section">
                <h1>{selectedModule.title}</h1>
                <p className="module-description">{selectedModule.description}</p>
              </div>

              <div className="lesson-list">
                <h2>{t('training.lessons')}</h2>
                {selectedModule.content.map(lesson => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    moduleId={selectedModule.id}
                    moduleName={selectedModule.title}
                    onComplete={() => handleContentComplete(selectedModule.id, lesson.id)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="welcome-section">
              <h1>{t('training.welcome')}</h1>
              <p>{t('training.selectModule')}</p>
              <div className="module-stats">
                <div className="stat">
                  <span className="stat-number">{modules.length}</span>
                  <span className="stat-label">{t('training.modulesAvailable')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">
                    {modules.reduce((sum, m) => sum + m.content.length, 0)}
                  </span>
                  <span className="stat-label">{t('training.totalLessons')}</span>
                </div>
                <div className="stat">
                  <span className="stat-number">
                    {Math.round(
                      modules.reduce((sum, m) => sum + calculateProgress(m), 0) / modules.length
                    )}%
                  </span>
                  <span className="stat-label">{t('training.yourProgress')}</span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * LessonCard Component
 * Displays individual lesson with embedded content
 */
function LessonCard({ lesson, onComplete, moduleId, moduleName }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showHandsOn, setShowHandsOn] = useState(false);
  const [handsOnExercise, setHandsOnExercise] = useState(null);
  const [loadingExercise, setLoadingExercise] = useState(false);
  const [slidesViewed, setSlidesViewed] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [quizPassed, setQuizPassed] = useState(false);
  const [practicalAnswered, setPracticalAnswered] = useState(false);

  useEffect(() => {
    // Load hands-on exercise when lesson is a quiz
    if (lesson.type === 'quiz' && moduleName) {
      console.log('[HandsOn] Loading exercise for:', moduleName);
      setLoadingExercise(true);
      fetch(`/api/training/hands-on?moduleName=${encodeURIComponent(moduleName)}`)
        .then(res => res.json())
        .then(data => {
          console.log('[HandsOn] Loaded exercise:', data);
          setHandsOnExercise(data);
          setLoadingExercise(false);
        })
        .catch(err => {
          console.warn('[HandsOn] Failed to load hands-on exercise:', err);
          setLoadingExercise(false);
        });
    }
  }, [lesson.type, moduleName]);

  const handleLessonComplete = () => {
    if (handsOnExercise && lesson.type === 'quiz') {
      setShowHandsOn(true);
    } else {
      onComplete();
    }
  };

  return (
    <div className="lesson-card">
      <div className="lesson-header" onClick={() => setIsOpen(!isOpen)}>
        <div className="lesson-title-section">
          <span className="lesson-type-badge">{lesson.type.toUpperCase()}</span>
          <h3>{lesson.title}</h3>
          <span className="lesson-duration">⏱ {lesson.duration}</span>
        </div>
        <div className="lesson-status">
          {lesson.completed && <span className="completed-badge">✅ {t('training.completed')}</span>}
          {showHandsOn && <span className="completed-badge">🛠 Hands-On Ready</span>}
          <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
        </div>
      </div>

      {isOpen && (
        <div className="lesson-content">
          {!showHandsOn ? (
            <>
              {renderContent({ ...lesson, type: lesson.type || lesson.content_type, moduleId, moduleName }, (seen, total) => { setSlidesViewed(v => Math.max(v, seen)); setTotalSlides(total); })}
              {!lesson.completed && (
                <button
                  onClick={handleLessonComplete}
                  className="complete-btn"
                  disabled={
                    (lesson.type === 'presentation' && slidesViewed < totalSlides) ||
                    (lesson.type === 'quiz' && (!quizPassed || loadingExercise)) ||
                    (showHandsOn && !practicalAnswered)
                  }
                  title={
                    lesson.type === 'presentation' && slidesViewed < totalSlides
                      ? `Watch all slides first (${slidesViewed}/${totalSlides})`
                      : lesson.type === 'quiz' && !quizPassed
                      ? 'Pass the quiz first (80% required)'
                      : loadingExercise
                      ? 'Loading practical exercises...'
                      : ''
                  }
                >
                  {loadingExercise ? '⏳ Loading exercises...' :
                   lesson.type === 'presentation' && slidesViewed < totalSlides ? `Watch slides: ${slidesViewed}/${totalSlides}` :
                   lesson.type === 'quiz' && !quizPassed ? 'Complete quiz (80% to pass)' :
                   t('training.markComplete')}
                </button>
              )}
            </>
          ) : (
            <>
              {handsOnExercise && (
                <HandsOnExercise
                  moduleTitle={moduleId}
                  exercise={handsOnExercise}
                  onComplete={() => {
                    setShowHandsOn(false);
                    onComplete();
                  }}
                />
              )}
              {!handsOnExercise && !loadingExercise && (
                <div style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <p>No hands-on exercises available for this module.</p>
                  <button
                    onClick={() => {
                      setShowHandsOn(false);
                      onComplete();
                    }}
                    className="complete-btn"
                  >
                    Complete Lesson
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Render different content types (presentations and quizzes)
 */
function renderContent(lesson, onProgress) {
  // Check if there's a presentation for this lesson
  const presentation = presentations[lesson.id];

  if (presentation) {
    return (
      <div className="presentation-container" style={{ flex: 1, minHeight: '700px', display: 'flex', flexDirection: 'column' }}>
        <PresentationPlayer slides={presentation.slides} title={presentation.title} onProgress={onProgress} />
      </div>
    );
  }

  switch (lesson.type) {
    case 'presentation':
      // Handle presentation type with slides
      if (lesson.slides && lesson.slides.length > 0) {
        return (
          <div className="presentation-container" style={{ flex: 1, minHeight: '700px', display: 'flex', flexDirection: 'column' }}>
            <PresentationPlayer slides={lesson.slides} title={lesson.title} onProgress={onProgress} />
          </div>
        );
      }
      // Fallback: display content as text if no slides
      if (lesson.content) {
        return (
          <div className="text-content" style={{ padding: '20px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            {lesson.content}
          </div>
        );
      }
      return <div className="placeholder">Presentation content loading...</div>;

    case 'video':
      return (
        <div className="video-container">
          {lesson.source ? (
            <iframe
              width="100%"
              height="400"
              src={lesson.source}
              title={lesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            <div className="placeholder">Video content loading...</div>
          )}
        </div>
      );

    case 'pdf':
      return (
        <div className="pdf-container">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              📋 Interactive presentation loaded above
            </p>
          </div>
        </div>
      );

    case 'html':
      return (
        <div className="html-container">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              📋 Interactive presentation loaded above
            </p>
          </div>
        </div>
      );

    case 'quiz':
      // Try to find quiz questions from i18n first, then fall back to hardcoded
      let questionsToUse = [];
      const moduleKey = lesson.moduleId || lesson.moduleName || lesson.id;

      // Try i18n translations first
      try {
        const translatedQuestions = t(`quizzes.${moduleKey}`, { returnObjects: true });
        if (Array.isArray(translatedQuestions) && translatedQuestions.length > 0) {
          questionsToUse = translatedQuestions;
        }
      } catch (e) {
        // Fall back to hardcoded
      }

      // Fall back to hardcoded quizQuestions
      if (questionsToUse.length === 0) {
        questionsToUse = quizQuestions[lesson.moduleId] ||
                        quizQuestions[lesson.moduleName] ||
                        quizQuestions[lesson.id] ||
                        quizQuestions[lesson.title] ||
                        lesson.questions || [];
      }

      console.log('[Quiz] moduleId:', lesson.moduleId, 'moduleName:', lesson.moduleName, 'lesson.id:', lesson.id, 'Questions found:', questionsToUse.length);

      return (
        <div className="quiz-container">
          <QuizComponent questions={questionsToUse} />
        </div>
      );

    default:
      return <div className="placeholder">Content type not supported</div>;
  }
}

/**
 * QuizComponent - Interactive quiz for knowledge checks
 */
function QuizComponent({ questions }) {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);

  if (questions.length === 0) {
    return <div className="placeholder">Quiz questions loading...</div>;
  }

  const currentQ = questions[currentQuestion];
  const isCorrect = selectedAnswer === currentQ?.correctAnswer;

  const handleAnswerClick = (index) => {
    if (!answered) {
      setSelectedAnswer(index);
      setAnswered(true);
      if (index === currentQ?.correctAnswer) {
        setCorrectCount(correctCount + 1);
      }
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setAnswered(false);
    } else {
      // Quiz complete
      const finalScore = Math.round((correctCount / questions.length) * 100);
      setScore(finalScore);
    }
  };

  return (
    <div className="quiz">
      {score === null ? (
        <div className="quiz-question">
          <h3>{t('quiz.question')} {currentQuestion + 1} {t('quiz.of')} {questions.length}</h3>
          <p>{currentQ?.question}</p>
          <div className="quiz-options">
            {currentQ?.options.map((option, i) => (
              <button
                key={i}
                className={`quiz-option ${selectedAnswer === i ? (isCorrect ? 'correct' : 'incorrect') : ''} ${answered ? 'disabled' : ''}`}
                onClick={() => handleAnswerClick(i)}
                disabled={answered}
                style={{
                  opacity: answered && selectedAnswer !== i ? 0.6 : 1,
                  cursor: answered ? 'not-allowed' : 'pointer',
                  backgroundColor:
                    selectedAnswer === i
                      ? isCorrect
                        ? '#4caf50'
                        : '#f44336'
                      : currentQ?.correctAnswer === i && answered
                      ? '#4caf50'
                      : '',
                  color: selectedAnswer === i || (currentQ?.correctAnswer === i && answered) ? 'white' : ''
                }}
              >
                {option}
              </button>
            ))}
          </div>
          {answered && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: isCorrect ? '#e8f5e9' : '#ffebee', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: isCorrect ? '#2e7d32' : '#c62828' }}>
                {isCorrect ? '✓ ' + t('quiz.correct') : '✗ ' + t('quiz.incorrect')}
              </p>
              {currentQ?.explanation && (
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#555' }}>
                  {currentQ.explanation}
                </p>
              )}
              <button
                onClick={handleNext}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#0066cc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {currentQuestion < questions.length - 1 ? t('quiz.nextQuestion') : t('quiz.seeResults')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="quiz-results">
          <h3>{t('quiz.complete')}</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#0066cc' }}>{score}%</p>
          <p>{t('quiz.youGot')} {correctCount} {t('quiz.correct_one')} {questions.length}</p>
        </div>
      )}
    </div>
  );
}
